import { LaunchArguments } from "@-/com.npmjs.react-native-launch-arguments";
import onetime from "@-/com.npmjs.onetime";

/**
 * The queue of canned utterances supplied at launch, one popped per microphone activation.
 *
 * WHY THIS EXISTS. A microphone cannot be emulated from an E2E run: Maestro drives the UI tree, and
 * there is no way to feed synthetic audio to the OS speech recognizer. Maestro's own JavaScript
 * (`evalScript`/`runScript`) runs in a SEPARATE engine on the runner — it cannot call into the app's
 * JS runtime — which is why the seed flow talks HTTP to a server rather than invoking app code.
 *
 * So the only way to cover a spoken drill end-to-end is to bypass the microphone in app logic and
 * drive everything downstream of it. Launch arguments are Maestro's documented channel for exactly
 * this, and `react-native-launch-arguments` is the library its docs prescribe:
 *
 *   - launchApp:
 *       appId: ${APP_ID}
 *       arguments:
 *         hanTestSpeechTranscripts: "我们|我们喜欢|我们喜欢吃苹果"
 *
 * A QUEUE rather than a single value, because the drills these serve are inherently multi-turn: the
 * user speaks, gets corrected, and speaks again. One fixed transcript could only ever exercise the
 * first attempt, so the interesting behaviour — resuming mid-sentence, hint escalation after a wrong
 * word, a word filled on the second try — would be unreachable from an E2E run. Each activation pops
 * the next entry, so a flow scripts a whole conversation by listing its turns.
 *
 * Entries are `|`-separated because Maestro launch arguments are flat scalars (string / boolean /
 * number) with no array support, and `|` cannot appear in Mandarin speech.
 *
 * What is bypassed is ONLY the microphone. Segmentation, the dictionary reading lookup, the
 * homophone matcher, the alignment, and the whole hint ladder all run exactly as they do for real
 * speech — so a flow tests the drill, not a stub of it.
 */
const SEPARATOR = "|";

/**
 * Split a launch-argument value into its utterances.
 *
 * Pulled out as a pure function because it is the only part of this module a unit test can reach:
 * jest cannot supply launch arguments, so the reader below always sees an empty queue there.
 */
export function parseTestSpeechTranscripts(raw: unknown): readonly string[] {
  if (typeof raw !== "string" || raw.length === 0) {
    return [];
  }
  return raw
    .split(SEPARATOR)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/**
 * Reading launch arguments throws when the native module is absent (web, jest, a build without
 * autolinking), so this is defensive and memoized: the value cannot change after launch, and a
 * throwing call per activation would be both wasteful and fragile.
 */
const getConfiguredTranscripts = onetime((): readonly string[] => {
  try {
    return parseTestSpeechTranscripts(
      LaunchArguments.value<{ hanTestSpeechTranscripts?: unknown }>().hanTestSpeechTranscripts,
    );
  } catch {
    return [];
  }
});

/** Position in the queue. Module-level so it survives the remounts a drill goes through. */
let nextTranscriptIndex = 0;

/** Whether this build was launched with canned speech — i.e. the mic should be stood in for. */
export function isTestSpeechEnabled(): boolean {
  return getConfiguredTranscripts().length > 0;
}

/**
 * Take the next canned utterance, or `null` once the queue is exhausted.
 *
 * Exhaustion returns `null` rather than repeating the last entry: a flow that activates the mic more
 * often than it scripted has diverged from what it meant to test, and silently replaying would make
 * that look like success.
 */
export function popTestSpeechTranscript(): string | null {
  const transcripts = getConfiguredTranscripts();
  const next = transcripts[nextTranscriptIndex];
  if (next === undefined) {
    return null;
  }
  nextTranscriptIndex += 1;
  return next;
}

/** Rewind the queue. For tests that exercise the popping itself. */
export function resetTestSpeechTranscriptQueue(): void {
  nextTranscriptIndex = 0;
}
